import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, inject } from '@angular/core';
import { ControlContainer, FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { MobilePaymentType } from '@sales/enums/mobile-payment-type';
import { SubscriptionBundleOption } from '@sales/modules/mobilephone/models/mobilephone-search-item';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { Price } from '@shared/models/price.model';
import { select, Store } from '@ngxs/store';
import { OfferingSelectors } from '@sales/states/offering/offering.selectors';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { Subscription } from 'rxjs';
import { MobilePhoneFormControls } from '../mobilephone-details.component';
import { DealerStateSelectors } from '@core/states/dealer/dealer-state.selectors';
import { PcmProduct } from '@app/sales/modules/subscription/models/pcm-product.model';
import { OfferingHelperService } from '@app/sales/services/offering-helper.service';
import {
  HardwareOfferingWithMobilbytteDTO,
  HardwareVariant,
} from '@app/sales/modules/mobilephone/models/mobilephone-variant';
import { CustomerSelectors, EquipmentUpgradeApprovedState } from '@app/sales/modules/details/states/customer.selectors';
import { NotDNumberValidator } from '@app/sales/validators/d-number.validator';
import { ssnValidator } from '@app/sales/validators/ssn.validator';
import { CustomerDownpaymentCheckAction } from '@app/sales/modules/details/states/customer.actions';
import { DownPaymentPayload } from '@app/core/models/initialize-response.model';
import { DealerClaimType } from '@app/core/enums/dealer-claim-type';
import { SetMobileBytteOfferingSelected } from '@app/sales/modules/mobilephone/states/mobilbytte/mobilbytte.actions';
import { PriceFormatPipe } from '@app/shared/pipes/price-format.pipe';
import { MobilbytteOfferingSelectorComponent } from './mobilbytte-offering-selector/mobilbytte-offering-selector.component';
import { MobilbytteBytteInfoComponent } from '@app/shared/components/mobilbytte-bytte-info/mobilbytte-bytte-info.component';
import { ExtendedCreditCheckComponent } from '@app/shared/components/extended-credit-check/extended-credit-check.component';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { AllowSettingsDirective } from '@app/shared/directives/allow-settings.directive';
import { UniqueSellingPointsComponent } from '../../../../../subscription/components/subscription-stepper/shared/unique-selling-points/unique-selling-points.component';
import { IcePillComponent } from '@app/shared/components/atoms/ice-pill/ice-pill.component';
import { MatIcon } from '@angular/material/icon';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { MobileUpfrontOfferingSelectorComponent } from './mobile-upfront-offering-selector/mobile-upfront-offering-selector.component';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import { noIllegalCharactersValidator } from '@app/sales/validators/no-illegal-characters-validator';
import { TradeInSelectors } from '@app/sales/states/customer-product-trade-in/customer-product-trade-in.selectors';
import { MatTooltip } from '@angular/material/tooltip';

const Messages = {
  MissingDetailedCreditCheck: 'Krever utvidet kredittsjekk',
  EquipmentUpgradeAlreadyInShoppingCart: 'Du kan kun ha en MobilBytte avtale i handlekurven',
  EquipmentUpgradeNotAllowed: 'Kredittsjekk feilet, kan ikke selge MobilBytte',
  TradeinInCart: 'Kan ikke velge MobilBytte når MobilPant er lagt til i handlekurven',
};

type MobilBytteOffering = {
  label: string;
  value: string;
  canOrderMobilBytteBytte: boolean;
  isWithinAvailableCredit: boolean;
  offering: HardwareOfferingWithMobilbytteDTO;
};

@Component({
  selector: 'app-mobilephone-payment-details',
  templateUrl: './mobilephone-payment-details.component.html',
  styleUrls: ['./mobilephone-payment-details.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    IceSelectBoxComponent,
    MobileUpfrontOfferingSelectorComponent,
    IceAlertComponent,
    MatIcon,
    IcePillComponent,
    UniqueSellingPointsComponent,
    AllowSettingsDirective,
    IceButtonComponent,
    ExtendedCreditCheckComponent,
    MobilbytteBytteInfoComponent,
    MobilbytteOfferingSelectorComponent,
    PriceFormatPipe,
    MatTooltip,
  ],
})
export class MobilephonePaymentDetailsComponent implements OnInit, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);
  private formBuilder = inject(FormBuilder);
  store = inject(Store);

  MobilePaymentType = MobilePaymentType;
  PriceType = PriceType;
  MobilePhoneFormControls = MobilePhoneFormControls;

  @Input() mobilePhoneVariant: HardwareVariant;
  @Input() hasMobilBytteProductInCart: boolean;
  @Input() isEquipmentUpgradeEnabled: boolean;
  @Input() isEquipmentUpgradePreCheckSuccessful: boolean;
  @Input() selectedPaymentType: MobilePaymentType;
  @Input() isMobilBytteBytte: boolean;
  @Input() existingProductsAvailableForEquipmentUpgrade: VoiceProduct[];
  @Input() allVoiceProducts: VoiceProduct[];
  @Input() isManualRegistration: boolean;
  @Input() canSellHardwareWithLogistics: boolean;
  @Input() canSellCellPhoneWithoutSubscription: boolean;
  @Input() canOnlySellMobilePhoneWithSubscription: boolean;

  @Output() paymentTypeChanged = new EventEmitter();
  @Output() equipmentUpgradeNewOfferingChanged = new EventEmitter();

  allVoiceOfferings = select(OfferingSelectors.getAllOfferings);
  isAccessoryKitEnabled = select(DealerStateSelectors.isAccessoryKitEnabled);
  equipmentUpgradeApproval = select(CustomerSelectors.equipmentUpgradeApproval);
  customer = select(CustomerSelectors.getState);
  hasPerformedDetailedCreditCheck = select(CustomerSelectors.hasPerformedDetailedCreditCheck);
  customerEquipmentUpgradeAllowance = select(CustomerSelectors.equipmentUpgradeAllowance);
  mobilBytteApproval = select(CustomerSelectors.mobilBytteApproval);
  hasPerformedAllowanceCheck = select(CustomerSelectors.hasPerformedAllowanceCheck);
  canPerformMobilBytteBytte = select(CustomerSelectors.canPerformMobilBytteBytte);
  hasTradeinInCart = select(TradeInSelectors.hasAcceptedTradeInVoucher);

  form: FormGroup;
  subscriptionBundleMinPrice: Price;
  selectedOfferingId: string;
  vasProduct: PcmProduct;
  EquipmentUpgradeApprovedState = EquipmentUpgradeApprovedState;
  DealerClaimType = DealerClaimType;

  selectedSubscriptionBundleOption: SubscriptionBundleOption;

  subscriptions$: Subscription[] = [];

  displayExtendedCreditCheckForm = false;

  customerCreditCheckForm = this.formBuilder.group({
    firstname: this.formBuilder.control('', [Validators.required, noIllegalCharactersValidator()]),
    lastname: this.formBuilder.control('', [Validators.required, noIllegalCharactersValidator()]),
    ssn: this.formBuilder.control('', [
      Validators.required,
      Validators.minLength(11),
      Validators.maxLength(11),
      ssnValidator(),
      NotDNumberValidator(),
    ]),
  });

  ngOnInit() {
    this.form = this.formGroupDirective.form;
    let isFirstMobilBytteOfferingValueChange = false;

    this.customerCreditCheckForm.patchValue({
      firstname: this.customer().privateCustomer.firstname,
      lastname: this.customer().privateCustomer.lastname,
    });

    if (this.mobilePhoneVariant && this.canOnlySellMobilePhoneWithSubscription) {
      this.form.get(MobilePhoneFormControls.PaymentType).setValue(MobilePaymentType.PayNowWithSubscription);
    }

    if (this.mobilePhoneVariant?.paymentInformation?.subscriptionBundleOptions?.length > 0) {
      this.subscriptionBundleMinPrice = new Price(
        Math.min(
          ...this.mobilePhoneVariant.paymentInformation.subscriptionBundleOptions.map(
            o => o.priceInformation.price.includingVAT
          )
        )
      );
    }

    this.subscriptions$.push(
      this.form.get(MobilePhoneFormControls.MobilBytteOffering)?.valueChanges.subscribe(value => {
        if (!value) return;
        const selectedOffering = this.mobilePhoneVariant?.mobilbytteOfferings.mobilbytteOfferings.find(
          o => o.offeringId === value
        );
        this.store.dispatch(new SetMobileBytteOfferingSelected(selectedOffering));
        if (isFirstMobilBytteOfferingValueChange) {
          this.form.get(MobilePhoneFormControls.NewOfferingId)?.setValue(null);
        }

        isFirstMobilBytteOfferingValueChange = true;
      })
    );
    this.subscriptions$.push(
      this.form.get(MobilePhoneFormControls.PaymentType).valueChanges.subscribe(paymentType => {
        if (this.form.value[MobilePhoneFormControls.PaymentType] !== paymentType) {
          this.form.get(MobilePhoneFormControls.NewOfferingId).setValue(null);
          this.initialize(paymentType);
          this.paymentTypeChanged.emit(paymentType);
          this.store.dispatch(new SetMobileBytteOfferingSelected(null));
        }
      })
    );

    this.initialize(this.form.get(MobilePhoneFormControls.PaymentType).value);
  }

  get mobilBytteDisabledReason(): string {
    if (this.hasMobilBytteProductInCart) {
      return Messages.EquipmentUpgradeAlreadyInShoppingCart;
    }
    if (this.hasTradeinInCart()) {
      return Messages.TradeinInCart;
    }
    return '';
  }

  get isTerminalCompatibleWithMobilbytte2(): boolean {
    return this.mobilePhoneVariant?.mobilbytteOfferings?.canHaveMobilbytte;
  }

  get isMobilBytteSelected(): boolean {
    return this.form.get(MobilePhoneFormControls.PaymentType).value === MobilePaymentType.EquipmentUpgrade;
  }

  get isMobilBytteAvailable(): boolean {
    if (this.isMobilBytteBytte) {
      return true;
    }

    return !!(
      this.mobilePhoneVariant?.mobilbytteOfferings?.mobilbytteOfferings?.some(o => this.isWithinAvailableCredit(o)) &&
      this.customer()?.authorizeResult?.equipmentUpgrade?.canBuyEquipmentUpgrade
    );
  }

  private isWithinAvailableCredit(offering: HardwareOfferingWithMobilbytteDTO): boolean {
    const availableCredit = this.customerEquipmentUpgradeAllowance()?.availableCredit || 0;
    return offering.totalPriceAfterAgreementPeriod?.amountIncludingVAT <= availableCredit;
  }

  get mobilBytteOfferings(): MobilBytteOffering[] {
    return this.mobilePhoneVariant?.mobilbytteOfferings.mobilbytteOfferings.map(o => {
      return {
        label: `${o.agreementPeriodInMonths} måneder`,
        value: o.offeringId,
        canOrderMobilBytteBytte: o.agreementPeriodInMonths !== 12,
        isWithinAvailableCredit: this.isMobilBytteBytte || this.isWithinAvailableCredit(o),
        offering: o,
      };
    });
  }

  get allowanceCheckFailed(): boolean {
    return (
      this.customer()?.hasPerformedAllowanceCheck &&
      (!this.customer().authorizeResult.approved || !this.customer().authorizeResult.subscriptionAllowance.approved)
    );
  }

  onExtendedCreditCheckClicked() {
    this.displayExtendedCreditCheckForm = true;
  }

  runCreditCheck() {
    if (this.customerCreditCheckForm.invalid) {
      return;
    }

    const ssn = this.customerCreditCheckForm.value.ssn;
    const { firstname, lastname } = this.customer().privateCustomer;
    if (this.customer().isExistingCustomer && this.customer().customerId) {
      const payload: DownPaymentPayload = {
        firstName: firstname,
        lastName: lastname,
        ssn: ssn,
        customerId: this.customer().customerId,
      };
      this.store.dispatch(new CustomerDownpaymentCheckAction(payload));
    }
  }

  private initialize(paymentType: MobilePaymentType) {
    if (paymentType === MobilePaymentType.PayNowWithoutSubscription) {
      this.form.get(MobilePhoneFormControls.NewOfferingId).disable();
      this.form.get(MobilePhoneFormControls.ExistingSubscriptionId).setValue(null);
      this.form.get(MobilePhoneFormControls.ExistingSubscriptionId).disable();
      this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade).setValue(null);
      this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade).disable();
      this.form.get(MobilePhoneFormControls.SubscriptionIdToUpgrade).setValue(null);
      this.form.get(MobilePhoneFormControls.SubscriptionIdToUpgrade).disable();
      this.form.get(MobilePhoneFormControls.MobilBytteOffering)?.disable();
      this.form.get(MobilePhoneFormControls.MobilBytteOffering)?.setValue(null);
    } else if (paymentType === MobilePaymentType.EquipmentUpgrade) {
      this.form.get(MobilePhoneFormControls.NewOfferingId).enable();
      this.form.get(MobilePhoneFormControls.ExistingSubscriptionId).setValue(null);
      this.form.get(MobilePhoneFormControls.ExistingSubscriptionId).disable();
      this.form.get(MobilePhoneFormControls.SubscriptionIdToUpgrade).setValue(null);
      this.form.get(MobilePhoneFormControls.SubscriptionIdToUpgrade).disable();

      this.form.get(MobilePhoneFormControls.MobilBytteOffering)?.enable();
    } else {
      this.form.get(MobilePhoneFormControls.NewOfferingId).enable();
      this.form.get(MobilePhoneFormControls.ExistingSubscriptionId).enable();
      this.form.get(MobilePhoneFormControls.IsSubscriptionUpgrade).enable();
      this.form.get(MobilePhoneFormControls.MobilBytteOffering)?.disable();
    }
  }

  get equipmentDisabledUpgradeTooltip() {
    if (this.isMobilBytteBytte) {
      return '';
    }
    if (!this.hasPerformedDetailedCreditCheck()) {
      return 'Krever utvidet kredittsjekk';
    }
    if (
      this.hasPerformedDetailedCreditCheck() &&
      this.equipmentUpgradeApproval() === EquipmentUpgradeApprovedState.NotApproved
    ) {
      return 'Kredittsjekk ikke godkjent for MobilBytte';
    }
    return null;
  }

  get mobilBytteLabel(): string {
    if (
      (this.allowanceCheckFailed ||
        this.hasMobilBytteProductInCart ||
        this.hasTradeinInCart() ||
        (this.customer()?.hasPerformedDetailedCreditCheck && !this.isMobilBytteAvailable)) &&
      !this.isMobilBytteBytte
    ) {
      return 'MobilBytte ikke tilgjengelig';
    }

    return 'MobilBytte';
  }

  get isMobilBytteReadonly(): boolean {
    return (
      (!this.isMobilBytteAvailable && this.hasPerformedDetailedCreditCheck()) ||
      this.hasMobilBytteProductInCart ||
      this.hasTradeinInCart() ||
      this.allowanceCheckFailed ||
      (this.isMobilBytteBytte && this.isMobilBytteSelected)
    );
  }

  onSelectedNewOfferingChanged(offeringId: string) {
    if (
      this.form.get(MobilePhoneFormControls.PaymentType).value === MobilePaymentType.PayNowWithSubscription &&
      !this.isManualRegistration
    ) {
      this.selectedSubscriptionBundleOption = this.mobilePhoneVariant.paymentInformation.subscriptionBundleOptions.find(
        o => o.subscriptionOfferingId === offeringId
      );
    }

    this.setVasProduct(offeringId);
  }

  onEquipmentUpgradeNewOfferingChanged(offeringId: string) {
    this.setVasProduct(offeringId);
    this.equipmentUpgradeNewOfferingChanged.emit(offeringId);
  }

  get isCustomerNameInvalid() {
    return this.customerCreditCheckForm.get('firstname')?.invalid || this.customerCreditCheckForm.get('lastname')?.invalid;
  }

  private setVasProduct(offeringId: string) {
    if (!this.allVoiceOfferings()) {
      return;
    }
    const offering = this.allVoiceOfferings().find(o => o.id === offeringId);
    this.vasProduct = OfferingHelperService.getVasProductFromOffering(offering);
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
